// src/steps/ReviewSubmitStep.tsx
import React, { useState } from "react";
import Button from "../components/ui/Button";
import type { WizardFormState } from "../wizard/wizardState";
import { getTshirtSizeOptions } from "../constants/tshirtSizes";
import { getVolunteerAvailabilityDayOptions } from "../constants/volunteerAvailabilityDays";
import { vbsSchedule } from "../constants/vbsSchedule";
import { termsAndConditions } from "../constants/termsAndConditions";
import { privacyNotice } from "../constants/privacyNotice";

type Props = {
  pathTitle: string;
  formState: WizardFormState;
  onEditStep: (targetIndex: number) => void;
  onSubmit: () => void;
  submitError?: string | null;
  submitting?: boolean;
};

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-2">
      <div className="text-sm font-medium text-slate-700">{label}</div>
      <div className="text-sm text-slate-900 text-right">{value}</div>
    </div>
  );
}

export default function ReviewSubmitStep({ pathTitle, formState, onEditStep, onSubmit, submitError, submitting }: Props) {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const p = formState.parentInfo;
  const volunteers = formState.volunteers;
  const children = formState.children;
  const emergency = formState.emergencyContacts;

  const tshirtMap = React.useMemo(
    () => Object.fromEntries(getTshirtSizeOptions().map(({ value, label }) => [value, label])),
    []
  );
  const availabilityDayMap = React.useMemo(
    () => Object.fromEntries(getVolunteerAvailabilityDayOptions().map(({ value, label }) => [value, label])),
    []
  );

  return (
    <div className="space-y-4">
      {/* Event Schedule Information */}
      <div className="rounded-xl bg-indigo-50 border border-indigo-200 p-3">
        <h3 className="text-sm font-bold text-indigo-900">📅 Event Schedule</h3>
        <div className="mt-2 space-y-2 text-sm text-indigo-800">
          <div>
            <div className="font-semibold">{vbsSchedule.mainEvent.name}</div>
            <div className="text-xs text-indigo-700">
              {vbsSchedule.mainEvent.dates} • {vbsSchedule.mainEvent.time}
            </div>
          </div>
          <div>
            <div className="font-semibold">{vbsSchedule.saturdayProgram.name}</div>
            <div className="text-xs text-indigo-700">
              {vbsSchedule.saturdayProgram.date} • {vbsSchedule.saturdayProgram.time}
            </div>
          </div>
          {volunteers.some((v) => v.firstName) && (
            <div className="mt-2 pt-2 border-t border-indigo-200 bg-indigo-100/50 rounded p-2">
              <div className="text-xs font-semibold text-indigo-900">🕒 Volunteer Arrival</div>
              <div className="text-xs text-indigo-700 mt-1">
                Please arrive at <strong>{vbsSchedule.volunteerInfo.arrivalTime}</strong> (30 minutes early)
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold">Summary</h3>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
            {pathTitle}
          </span>
        </div>
        <p className="mt-2 text-xs text-slate-600">
          Review what you entered. If something is wrong, go back or tap edit.
        </p>
      </div>

      {(p.firstName || p.lastName) && (<div className="rounded-xl border border-slate-200 p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold">Parent Info</h3>
          <Button type="button" variant="ghost" onClick={() => onEditStep(0)} className="px-3 py-2">
            Edit
          </Button>
        </div>
        <div className="mt-2">
          <Row label="Parent Name" value={`${p.firstName} ${p.lastName}`.trim()} />
          <Row label="Email" value={p.email} />
          <Row label="Phone" value={p.phone} />
          <Row label="Relation" value={p.relationToChild} />
          <Row label="Pickup Type" value={p.pickupType} />
        </div>
      </div>
      )}

      {volunteers.some((v) => v.firstName || v.lastName) && (
        <div className="space-y-3">
          {volunteers.map((v, idx) => {
            if (!v.firstName && !v.lastName) return null;
            const availabilityDisplay = v.availabilityDays
              .map((day) => availabilityDayMap[day] || day)
              .join(", ");
            return (
              <div key={idx} className="rounded-xl border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold">
                    {volunteers.length > 1 ? `Volunteer ${idx + 1}` : "Volunteer Info"}
                  </h3>
                  <Button type="button" variant="ghost" onClick={() => onEditStep(0)} className="px-3 py-2">
                    Edit
                  </Button>
                </div>
                <div className="mt-2">
                  <Row label="Name" value={`${v.firstName} ${v.lastName}`.trim()} />
                  <Row label="Email" value={v.email} />
                  <Row label="Phone" value={v.phone} />
                  <Row label="Address" value={`${v.street}, ${v.city}, ${v.state} ${v.zip}`.trim()} />
                  <Row label="Age Group" value={v.ageGroup === 'under18' ? 'Under 18' : '18+'} />
                  {v.ageGroup === 'under18' && <Row label="Grade" value={v.grade} />}
                  <Row label="Tshirt Size" value={tshirtMap[v.tshirtSize] || v.tshirtSize} />
                  <Row label="Church" value={v.church} />
                  <Row label="Available Days" value={availabilityDisplay || "None selected"} />
                  <Row label="Availability Notes" value={v.availabilityNotes || "None"} />
                  {children[0].firstName && (
                    <>
                      <Row label="Relation" value={v.relationToChild} />
                      <Row label="Pickup Type" value={v.pickupType} />
                    </>
                  )}
                  <Row label="Comments" value={v.comments || "None"} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {children[0].firstName && (
        <div className="rounded-xl border border-slate-200 p-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold">Children</h3>
            <Button type="button" variant="ghost" onClick={() => onEditStep(1)} className="px-3 py-2">
              Edit
            </Button>
          </div>
          <div className="mt-2 space-y-3">
            {children.map((c, idx) => (
              <div key={idx} className="rounded-lg bg-slate-50 p-3">
                <div className="text-sm font-semibold">
                  Child {idx + 1}: {`${c.firstName} ${c.lastName}`.trim()}
                </div>
                <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-slate-700 sm:grid-cols-2">
                  <div>Age: {c.age}</div>
                  <div>Grade: {c.grade}</div>
                  <div>Requested Group: {c.requestedGroup || "None selected"}</div>
                  <div>Allergies: {c.allergies || "None"}</div>
                  <div>Special Needs: {c.specialNeeds || "None"}</div>
                  <div>Tshirt Size: {tshirtMap[c.tshirtSize] || c.tshirtSize}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold">Emergency Contacts</h3>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onEditStep(children[0].firstName ? 2 : 1)}
            className="px-3 py-2"
          >
            Edit
          </Button>
        </div>
        <div className="mt-2 space-y-3">
          {emergency.map((c, idx) => (
            <div key={idx} className="rounded-lg bg-slate-50 p-3">
              <div className="text-sm font-semibold">
                {`${c.firstName} ${c.lastName}`.trim()}
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-slate-700 sm:grid-cols-2">
                <div>Phone: {c.phone}</div>
                <div>Relation: {c.relation}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {submitError ? (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{submitError}</div>
      ) : null}

      {/* Terms and Conditions Modal */}
      {showTerms && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="text-lg font-bold">Terms and Conditions</h2>
            <p className="mt-2 text-sm whitespace-pre-line">
              {termsAndConditions}
            </p>
            <Button type="button" onClick={() => setShowTerms(false)} className="mt-4">Close</Button>
          </div>
        </div>
      )}

      {/* Privacy Notice Modal */}
      {showPrivacy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="text-lg font-bold">Privacy Notice</h2>
            <p className="mt-2 text-sm whitespace-pre-line">
              {privacyNotice}
            </p>
            <Button type="button" onClick={() => setShowPrivacy(false)} className="mt-4">Close</Button>
          </div>
        </div>
      )}

      {/* Terms Acceptance Checkbox */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="acceptTerms"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
        />
        <label htmlFor="acceptTerms" className="text-sm text-slate-700">
          I agree to the{" "}
          <button
            type="button"
            onClick={() => setShowTerms(true)}
            className="text-blue-600 underline"
          >
            terms and conditions
          </button>{" "}
          and acknowledge the{" "}
          <button
            type="button"
            onClick={() => setShowPrivacy(true)}
            className="text-blue-600 underline"
          >
            privacy notice
          </button>
        </label>
      </div>

      <Button type="button" className="w-full" onClick={onSubmit} disabled={submitting || !acceptedTerms}>
        {submitting ? "Submitting..." : "Submit Registration"}
      </Button>
    </div>
  );
}
